import { Injectable } from '@angular/core';
import { SessionKeyProvider } from './session-key-provider';

interface CacheEntry<T> {
  id: string;
  sessionId: string;
  data: T;
}

@Injectable({
  providedIn: 'root',
})
export class SessionCacheService {
  private readonly dbName = 'session-store-db';

  private readonly storeName = 'session-store';
  private dbPromise: Promise<IDBDatabase>;

  constructor(private sessionKeyProvider: SessionKeyProvider) {
    this.dbPromise = this.init();
  }

  /**
   * Writes data to cache by cache key.
   * Raises any error occured during write
   * @param cacheKey
   * @param data
   * @returns
   */
  async writeAsync<T>(cacheKey: string, data: T): Promise<void> {
    const database = await this.dbPromise;
    const composedCacheKey = `${this.sessionKeyProvider.currentSessionId}-${cacheKey}`;

    return new Promise((resolve, reject) => {
      const transaction: IDBTransaction = database.transaction([this.storeName], 'readwrite');
      const objectStore: IDBObjectStore = transaction.objectStore(this.storeName);

      const cacheEntry: CacheEntry<T> = {
        id: composedCacheKey,
        sessionId: this.sessionKeyProvider.currentSessionId,
        data: data,
      };

      const request: IDBRequest<IDBValidKey> = objectStore.put(cacheEntry);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = (event: Event) => {
        reject((event.target as IDBRequest).error);
      };
    });
  }

  /**
   * Safely writes cache key. Doesn't raise any error.
   * Returns true if write operation is successful.
   * Returns false if write operation failed.
   * @param cacheKey 
   * @param data 
   * @returns 
   */
  async safeWriteAsync<T>(cacheKey: string, data: T): Promise<boolean> {
    try {
      await this.writeAsync(cacheKey, data);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Reads cache by cache key.
   * Returns null when cache does not exists.
   * Raises any error occured during cache read
   * @param cacheKey
   * @returns
   */
  async readAsync<T>(cacheKey: string): Promise<T> {
    const database = await this.dbPromise;
    const composedCacheKey = `${this.sessionKeyProvider.currentSessionId}-${cacheKey}`;

    const result: T = await new Promise((resolve, reject) => {
      const transaction: IDBTransaction = database.transaction([this.storeName], 'readonly');
      const objectStore: IDBObjectStore = transaction.objectStore(this.storeName);
      const request: IDBRequest<CacheEntry<any> | undefined> = objectStore.get(composedCacheKey);

      request.onsuccess = (event: Event) => {
        const cacheEntry = (event.target as IDBRequest<CacheEntry<any> | undefined>).result;

        if (!cacheEntry) {
          resolve(null);
          return;
        }

        resolve(cacheEntry.data);
      };

      request.onerror = (event: Event) => {
        reject((event.target as IDBRequest).error);
      };
    });

    return result;
  }

  /**
   * Safely reads cache by cache key.
   * If any error occurs, or cache does not exist, returns null.
   * @param cacheKey
   * @returns
   */
  async safeReadAsync<T>(cacheKey: string): Promise<T> {
    try {
      return await this.readAsync(cacheKey);
    } catch {
      return null;
    }
  }

  async deleteCacheEntriesByKeyPrefixAsync(keyPrefix: string): Promise<string[]> {
    const database = await this.dbPromise;
    const composedKeyPrefix = `${this.sessionKeyProvider.currentSessionId}-${keyPrefix}`;

    return new Promise((resolve, reject) => {
      const transaction: IDBTransaction = database.transaction([this.storeName], 'readwrite');
      const objectStore: IDBObjectStore = transaction.objectStore(this.storeName);

      const request: IDBRequest<IDBValidKey[]> = objectStore.getAllKeys();

      request.onsuccess = (event: Event) => {
        const idbValidKeys: IDBValidKey[] = (event.target as IDBRequest).result;
        const keysToDelete = idbValidKeys
          .map((cacheEntry) => cacheEntry.toString())
          .filter((cacheKey) => cacheKey.startsWith(composedKeyPrefix));

        keysToDelete
          .forEach((cacheKey) => objectStore.delete(cacheKey));

        resolve(keysToDelete);
      };

      request.onerror = (event: Event) => {
        reject((event.target as IDBRequest).error);
      };
    });
  }

  private async clearAllAsync(): Promise<void> {
    const database = await this.dbPromise;

    return new Promise((resolve, reject) => {
      const transaction: IDBTransaction = database.transaction([this.storeName], 'readwrite');
      const objectStore: IDBObjectStore = transaction.objectStore(this.storeName);

      const request: IDBRequest<IDBValidKey[]> = objectStore.getAllKeys();

      request.onsuccess = (event: Event) => {
        const idbValidKeys: IDBValidKey[] = (event.target as IDBRequest).result;
        const keysToDelete = idbValidKeys
          .map((cacheEntry) => cacheEntry.toString())
          // we take ONLY cache keys that don't start with the current cache key
          .filter((cacheKey) => !cacheKey.startsWith(this.sessionKeyProvider.currentSessionId));

        keysToDelete
          .forEach((cacheKey) => objectStore.delete(cacheKey));

        resolve();
      };

      request.onerror = (event: Event) => {
        reject((event.target as IDBRequest).error);
      };
    });
  }

  private createDatabaseAsync(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onsuccess = (event) => {
        resolve((event.target as any).result as IDBDatabase);
      };

      request.onerror = (event) => {
        reject((event.target as any).error);
      };

      request.onupgradeneeded = (event) => {
        const database: IDBDatabase = (event.target as any).result;
        // Set up object store and indexes if needed
        if (!database.objectStoreNames.contains(this.storeName)) {
          database.createObjectStore(this.storeName, { keyPath: 'id' });
        }
      };
    });
  }

  private async init(): Promise<IDBDatabase> {
    const database = await this.createDatabaseAsync();

    this.clearAllAsync();

    return database;
  }
}
